import { useContext } from 'react';

import { ArticleContext } from '../Article.context';
import JSXStyle from 'styled-jsx/style';

export const StyledDivider = (props: StyledDividerProps) => {
	const { id, bleed, style, expandfullwidth } = props;
	return (
		<Divider
			id={id}
			bleed={bleed === 'on'}
			StyleID={style}
			expandFullWidth={expandfullwidth === 'on'}
		/>
	);
};

interface StyledDividerProps {
	id: string;
	bleed: string;
	margin: string;
	style: any;
	expandfullwidth: string;
}

export const Divider = (props: DividerProps) => {
	const { id, bleed = false, expandFullWidth, StyleID } = props;
	const { style } = useContext(ArticleContext);

	const { css } = getStyle(clone(style?.properties), StyleID, id);

	const containerStyle: any = {
		clear: 'both',
	};
	const componentStyle: any = {};

	if (expandFullWidth) {
		componentStyle.minWidth = '100%';
		componentStyle.width = '100%';
	}

	const classNames = [`jsx-${id}`];
	if (bleed) {
		classNames.push('bleed');
	}

	return (
		<div style={containerStyle} className={classNames.join(' ')}>
			<hr style={componentStyle} />
			<JSXStyle id={id}>{css}</JSXStyle>
		</div>
	);
};

interface DividerProps {
	id: string;
	bleed: boolean;
	expandFullWidth: boolean;
	StyleID: string;
}

function getStyle(style: any, StyleID: string, id: string) {
	const response: any = {
		margin: 0,
		css: '',
	};
	if (!style) {
		return response;
	}

	let dividerStyle = style['divider'];
	const styles = style['dividers'];

	if (StyleID && styles && styles.length) {
		for (const s of styles) {
			if (`${s.id}` === `${StyleID}`) {
				dividerStyle = mergeDeep(dividerStyle, s);
				break;
			}
		}
	}

	let color = dividerStyle?.color
		? `#${dividerStyle.color.replace('#', '')}`
		: 'black';
	let thickness = dividerStyle?.thickness
		? `${dividerStyle.thickness}px`
		: '1px';
	let margin: any = [];
	if (dividerStyle?.marginTop) {
		margin.push(
			`margin-top: ${dividerStyle?.marginTop}${dividerStyle?.unit
				?.margintTop || 'px'};`,
		);
	}
	if (dividerStyle?.marginBottom) {
		margin.push(
			`margin-bottom: ${dividerStyle?.marginBottom}${dividerStyle?.unit
				?.margintBottom || 'px'};`,
		);
	}

	margin = margin.join('\n');
	const width = dividerStyle?.width ? dividerStyle.width : 100;

	response.css = `.jsx-${id} hr{
		border: 0px;
		width: ${width}%;
		box-sizing: content-box;
	}

	.jsx-${id} {
		${margin}
	}`;

	let overwriteStyle: any = {};

	for (const k in styles) {
		const s = styles[k];
		if (s.id === StyleID) {
			overwriteStyle = s;
			break;
		}
	}

	let css = '';
	if (overwriteStyle.css) {
		css = `.jsx-${id} hr {${overwriteStyle.css}}`;
	}

	if (dividerStyle.style) {
		switch (dividerStyle.style) {
			case 'blur':
				response.css += `.jsx-${id} hr {
					height: 0;
					box-shadow: 0 0 10px 1px ${color};
				}
				.jsx-${id} hr:after{
					content: "\\00a0";
				}`;
				break;
			case 'dashed':
				response.css += `.jsx-${id} hr {
					border-bottom: ${thickness} dashed ${color};
				}`;
				break;
			case 'dotted':
				response.css += `.jsx-${id} hr {
				border-bottom: ${thickness} dotted ${color};
			}`;
				break;
			case 'chapter':
				response.css += `.jsx-${id} hr {
						padding: 0;
						border-top: medium double ${color};
						color: ${color};
						text-align: center;
					}
					.jsx-${id} hr:after {
						content: "§";
						display: inline-block;
						position: relative; 
						top: -0.7em;  
						font-size: 1.5em;
						padding: 0 0.25em;
					}`;
				break;
			case 'elegant':
				response.css += `.jsx-${id} hr {
					height: 20px;
					border-style: solid;
					border-color: ${color};
					border-width: 1px 0 0 0;
					border-radius: 20px;
				}
				.jsx-${id} hr:before {
					display: block;
					content: "";
					height: 20px;
					margin-top: -21px;
					border-style: solid;
					border-color: ${color};
					border-width: 0 0 1px 0;
					border-radius: 20px;
				}`;
				break;
			case 'innerfade':
				response.css += `.jsx-${id} hr {
					height: 2px;
					background: ###color#;
					background-image: -webkit-linear-gradient(left, #fff, ${color}, #fff);
					background-image: linear-gradient(left, #fff, ${color}, #fff);
				}`;
				break;
			case 'inset':
				response.css += `.jsx-${id} hr {
					height: 0;
					border-top: 1px solid rgba(0, 0, 0, 0.1);
					border-bottom: 1px solid rgba(255, 255, 255, 0.3);
				}`;
				break;
			case 'shadow':
				response.css += `.jsx-${id} hr{
					height: 12px;
					  box-shadow: inset 0 10px 12px -12px rgba(0,0,0,0.5);
				}`;
				break;
			default:
				response.css += `.jsx-${id} hr{
					border-bottom: ${thickness} solid ${color};
				}`;
		}
	}

	if (dividerStyle) {
		switch (dividerStyle.align) {
			case 'left':
				response.css += `.jsx-${id} hr {
                    margin-left: 0px;
                }`;
				break;
			case 'right':
				response.css += `.jsx-${id} hr {
                    margin-right: 0px;
                }`;
				break;
			default:
				break;
		}
	}

	if (!response.css) {
		response.css = css;
	}
	return response;
}

function mergeDeep(target: any, ...sources: any): any {
	if (!sources.length) return target;
	const source = sources.shift();

	if (isObject(target) && isObject(source)) {
		for (const key in source) {
			if (isObject(source[key])) {
				if (!target[key]) Object.assign(target, { [key]: {} });
				mergeDeep(target[key], source[key]);
			} else {
				Object.assign(target, { [key]: source[key] });
			}
		}
	}

	return mergeDeep(target, ...sources);
}

function isObject(item: any) {
	return item && typeof item === 'object' && !Array.isArray(item);
}

function clone(obj: any) {
	if (structuredClone) {
		return structuredClone(obj);
	}
	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}
